import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import {
  Button,
  Input,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';

import ModalTemplatesCustomGroupHeading from './ModalTemplatesCustomGroupHeading';
import UpgradePlanButton from '../UpgradePlanButton';

import {
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';

const ShareModalTemplates = ({
  template,
  templates,
  templatesLoading,
  handleChangeTemplate,
  handleAddingTemplate,
  simple,
  openSettings,
  addingTemplate,
  name,
  nameError,
  saving,
  handleName,
  handleDestination,
  handleClose,
}) => {
  const options = templates.map(p => ({ label: p.name, value: p.uuid, type: p.type }));

  const intl = useIntl();

  const nameRef = useRef(null);

  useEffect(() => {
    if (addingTemplate && nameRef.current) {
      nameRef.current.focus();
    }
  }, [addingTemplate]);

  const handleAddButton = () => {
    if (simple) {
      openSettings();
      handleAddingTemplate(true);
    } else {
      handleAddingTemplate(true);
    }
  };

  const selectOptions = [
    {
      label: 'user',
      options: options.filter(o => o.type === 'user'),
    },
    {
      label: 'workspace',
      options: options.filter(o => o.type === 'workspace'),
    },
  ];

  if (!getSetting('share_templates_user_create')) {
    return null;
  }

  return (
    <div className={classnames('share-templates share-setting form-inline', { 'share-templates-simple': simple })}>
      <Label>
        {intl.formatMessage({ defaultMessage: 'Select template' })}
        <UpgradePlanButton
          feature="share-templates"
          simple={true}
        />
      </Label>
      <div className="share-templates-select-wrapper">
        <Select
          className="select-template"
          classNamePrefix="select-template"
          value={template ? options.find(o => o.value === template) : null}
          menuPortalTarget={document.body}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          options={selectOptions}
          onChange={value => handleChangeTemplate(value)}
          isLoading={templatesLoading}
          isDisabled={saving || !userHasFeature('share-templates')}
          components={{
            GroupHeading: (props) => (
              <ModalTemplatesCustomGroupHeading
                {...props}
                handleClose={handleClose}
              />
            ),
          }}
        />
        <Button
          color="secondary"
          id="AddNewShareTemplate"
          disabled={saving || !userHasFeature('share-templates')}
          onClick={handleAddButton}
        >
          <i className="far fa-plus" />
        </Button>
        <UncontrolledTooltip fade={false} placement="top" target="AddNewShareTemplate">
          {intl.formatMessage({ defaultMessage: 'Create new template' })}
        </UncontrolledTooltip>
      </div>
      {addingTemplate && (
        <>
          <div className="share-templates-add-new-info">
            <p>{intl.formatMessage({ defaultMessage: 'You\'re creating a new template.' })}</p>
          </div>
          <Label for="shareTemplateName" className="share-templates-label">
            {intl.formatMessage({ defaultMessage: 'Name' })}
          </Label>
          <Input
            innerRef={nameRef}
            type="text"
            name="name"
            id="shareTemplateName"
            value={name}
            onChange={e => handleName(e.target.value)}
            placeholder={intl.formatMessage({ defaultMessage: 'Template name' })}
          />
          {nameError && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {intl.formatMessage({ defaultMessage: 'Please provide valid name.' })}
            </div>
          )}
          {(userIsAdmin() || userIsManager()) && (
            <>
              <Label for="templateType" className="share-templates-label">
                {intl.formatMessage({ defaultMessage: 'Destination' })}
              </Label>
              <Input
                type="select"
                className="custom-select"
                name="templateType"
                onChange={(e) => { handleDestination(e.target.value); }}
              >
                <option value="user">{intl.formatMessage({ defaultMessage: 'User settings' })}</option>
                <option value="workspace">{intl.formatMessage({ defaultMessage: 'Workspace settings' })}</option>
              </Input>
            </>
          )}
        </>
      )}
    </div>
  );
};

ShareModalTemplates.propTypes = {
  simple: PropTypes.bool,
  template: PropTypes.string,
  templates: PropTypes.array.isRequired,
  templatesLoading: PropTypes.bool,
  handleChangeTemplate: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAddingTemplate: PropTypes.func.isRequired,
  openSettings: PropTypes.func,
  addingTemplate: PropTypes.bool,
  saving: PropTypes.bool.isRequired,
  name: PropTypes.string,
  nameError: PropTypes.bool,
  handleName: PropTypes.func,
  handleDestination: PropTypes.func,
};

ShareModalTemplates.defaultProps = {
  template: null,
  templatesLoading: false,
  simple: false,
  addingTemplate: false,
  name: null,
  nameError: false,
  openSettings: () => {},
  handleName: () => {},
  handleDestination: () => {},
};

export default ShareModalTemplates;
